import React from 'react';
import { Card, CardBody, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './DashboardStats.css';

interface StatCardProps {
  title: string;
  value: number;
  icon: string;
  color: string;
  showArrow?: boolean;
  isLastInRow?: boolean;
  linkTo?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, showArrow = false, isLastInRow = false, linkTo }) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (linkTo) {
      navigate(linkTo);
    }
  };

  return (
    <div className="stat-card-wrapper">
      <Card 
        className={`stat-card h-100 border-0 shadow-sm ${linkTo ? 'clickable-card' : ''}`}
        onClick={handleCardClick}
      >
        <CardBody className="p-3">
          <div className="d-flex align-items-center">
            {/* Icon on the left */}
            <div className={`stat-icon bg-${color}-subtle rounded d-flex align-items-center justify-content-center me-3`}>
              <i className={`${icon} text-${color}`} style={{ fontSize: '1.25rem' }}></i>
            </div>
            
            {/* Text content on the right */}
            <div className="flex-grow-1">
              <h4 className={`mb-1 fw-bold text-${color}`}>{value.toLocaleString()}</h4>
              <h6 className="text-muted mb-0 small">{title}</h6>
            </div>
            
            {/* Arrow on the far right */}
            {showArrow && !isLastInRow && (
              <div className="connecting-arrow">
                <i className="ph-arrow-right text-muted"></i>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

interface DashboardStatsProps {
  stats: {
    teams: number;
    players: number;
    tournaments: number;
    payments: number;
    activePlayers: number;
    inactivePlayers: number;
    activeTeams: number;
    inactiveTeams: number;
  };
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ stats }) => {
  // First row of cards - matching the design exactly with navigation links
  const firstRowCards = [
    {
      title: "Teams",
      value: stats.teams,
      icon: "ph-users-three",
      color: "warning", // Yellow - group of people icon
      linkTo: "/my-club/team-information"
    },
    {
      title: "Players in total",
      value: stats.players,
      icon: "ph-users-three",
      color: "primary", // Blue - three stylized figures
      linkTo: "/my-club/player-information"
    },
    {
      title: "Tournaments",
      value: stats.tournaments,
      icon: "ph-shield",
      color: "success", // Green - shield icon
      linkTo: "/tournaments"
    },
    {
      title: "Payments",
      value: stats.payments,
      icon: "ph-stack",
      color: "warning", // Orange - stack of coins
      linkTo: "/payment-transactions/transactions"
    }
  ];

  return (
    <div className="dashboard-stats-container mb-4">
      {/* First Row */}
      <Row className="mb-3">
        {firstRowCards.map((stat, index) => (
          <Col lg={3} md={6} sm={6} key={index} className="mb-3">
            <StatCard 
              {...stat} 
              showArrow={index < firstRowCards.length - 1}
              isLastInRow={index === firstRowCards.length - 1}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default DashboardStats; 